import { createRow, MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { useParams } from "react-router";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";


const FeuB4OnShorePowerSupply = forwardRef(({ b4OnShorePowerSupplyValues, refetchMonitoringData, b4OnShorePowerSupplydata,vesselMstId }, ref) => {

    const RowActions = {
        Edit: 0,
        Add: 1,
    };
    const [activeAction, setactiveAction] = useState(-1);
    const [opsData, setOpsData] = useState([]);
    const [validationErrors, setValidationErrors] = useState({});
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingRowIndex, setDeletingRowIndex] = useState();

    const asNullable = (n) => (n === '' || n == null ? null : Number(n));



    useEffect(() => {
        if (Array.isArray(b4OnShorePowerSupplydata)) {
            const transformedData = b4OnShorePowerSupplydata?.map((item) => {
                const technicalDesc = item?.technicalDetails || {};
                return {
                    opsEquipmentReferenceNo: item?.opsEquipmentReferenceNo || '',
                    opsEquipmentName: item?.opsEquipmentName || '',
                    technicalDetails: {
                        opsEquipmentType: technicalDesc.opsEquipmentType || '',
                        ratingPowerKW: technicalDesc.ratingPowerKW ?? 0,
                        frequencyHz: technicalDesc.frequencyHz ?? 0,
                        voltageV: technicalDesc.voltageV ?? 0,
                        equipmentDescription: technicalDesc.equipmentDescription || '',
                    },
                    standard: item?.standard || '',
                    otherStandards: item?.otherStandards || '',

                };
            });

            setOpsData((transformedData));
        }
    }, [b4OnShorePowerSupplydata]);

    const handleDeleteBtnClick = (rowIndex) => {
        setConfirmDelete(true);
        setDeletingRowIndex(rowIndex);
    };

    const handleOnShoreActions = (table, row, action) => {
        if (activeAction !== -1) return;

        if (action === RowActions.Add) {
            const defaultRow = createRow(table, {
                opsEquipmentReferenceNo: "",
                opsEquipmentName: '',
                technicalDetails: {
                    opsEquipmentType: '',
                    ratingPowerKW: null,
                    frequencyHz: null,
                    voltageV: null,
                    equipmentDescription: '',
                },
                standard: '',
                otherStandards: '',
            });

            table.setCreatingRow(defaultRow);
            setactiveAction(action);
            return;
        }

        if (action === RowActions.Edit) {
            table.setEditingRow(row);
            setactiveAction(action);
        }
    };

    const resetActiveRow = () => {
        setactiveAction(-1);
        setValidationErrors({});
    };

    const validateRequired = (value) => {
        if (typeof value === "string") {
            return !!value?.length;
        } else if (typeof value === "number") {
            return value >= 0;
        }
        return false;
    };

    const getStandards = () => {
        return (
            b4OnShorePowerSupplyValues?.opsStandards?.map((type) => ({
                label: type.value,
                value: type.code,
            })) || []
        );
    };
    const getOpsEquipmentType = () => {
        return (
            b4OnShorePowerSupplyValues?.opsEquipementTypes?.map((type) => ({
                label: type.value,
                value: type.code,
            })) || []
        );
    };

    const opsEquipmentTypeOptions = useMemo(getOpsEquipmentType, [b4OnShorePowerSupplyValues]);
    const standardsOptions = useMemo(getStandards, [b4OnShorePowerSupplyValues]);

    const typeLabelByCode = useMemo(
        () => Object.fromEntries(opsEquipmentTypeOptions.map(o => [o.value, o.label])),
        [opsEquipmentTypeOptions],
    );

    const standardLabelByCode = useMemo(
        () => Object.fromEntries(standardsOptions.map(o => [o.value, o.label])),
        [standardsOptions],
    );


    const validateOps = (row) => {
        const errors = {};
        if (!validateRequired(row["technicalDetails.opsEquipmentType"])) {
            errors.opsEquipementType = "Field is required";
        }
        return errors;
    };

    useImperativeHandle(ref, () => ({
        async submitAndCheck() {
            const { creatingRow, editingRow } = table.getState();
            if (!creatingRow && !editingRow) return true;
            const mode = creatingRow ? "add" : "edit";
            const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
            let isDirty = false;
            if (editingRow && editedValues) {
                isDirty = Object.keys(editedValues).some(
                    (key) => editedValues[key] !== editingRow.original[key]
                );
            }
            const validationRes = validateOps(editedValues, mode, {});
            const hasErrors = Object.values(validationRes).some((err) => err);
            if (hasErrors) {
                setValidationErrors(validationRes);
                emitToasterMsg(
                    "warning",
                    "Validation Error",
                    "Please correct the highlighted fields before proceeding."
                );
                return false;
            }
            if (creatingRow) {
                await handleCreateOpsOnShorePowerSupply({ values: editedValues, table }, true);
                return true;
            } else if (editingRow && isDirty) {
                await handleUpdateOpsOnShorePowerSupply({ values: editedValues, table, row: editingRow }, true);
                return true;
            }
            if (!isDirty) {
                setactiveAction(-1)
                table.setEditingRow(null);
                return true;
            }
        }
    }));

    const handleCreateOpsOnShorePowerSupply = async ({ values, table }, isAuto = false) => {

        const newValidationErrors = validateOps(values, "add", {});
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});

        const newRow = {
            b4OnshorePowerSupplyEquipmentId: 0,
            opsEquipmentReferenceNo: values?.opsEquipmentReferenceNo,
            opsEquipmentName: values?.opsEquipmentName,
            technicalDetails: {
                opsEquipmentType: values.technicalDetails?.opsEquipmentType
                    || values["technicalDetails.opsEquipmentType"]
                    || '',
                ratingPowerKW: values["technicalDetails.ratingPowerKW"] === '' ?
                    0 :
                    Number(values["technicalDetails.ratingPowerKW"]),
                frequencyHz: values["technicalDetails.frequencyHz"] === '' ?
                    0 :
                    Number(values["technicalDetails.frequencyHz"]),
                voltageV: values["technicalDetails.voltageV"] === '' ?
                    0 :
                    Number(values["technicalDetails.voltageV"]),
                equipmentDescription: values["technicalDetails.equipmentDescription"]
            },
            standard: values?.standard,
            otherStandards: values?.otherStandards,
        };
        const payload = opsData?.map((item) => ({
            ...item,
            b4OnshorePowerSupplyEquipmentId: 0
        }));
        const finalPayload = ([...payload, newRow]);
        try {
            const b4OnShorePowerSupply = await callApi({
                method: "POST",
                url: API_ROUTES.B4(vesselMstId),
                data: finalPayload,
            });
            if (b4OnShorePowerSupply?.isSuccess) {
                emitToasterMsg("success", "Table B.4", isAuto ? "On Shore Power Supply Equipment has been autosaved successfully" : "On Shore Power Supply Equipment has been added successfully");
                table.setCreatingRow(null);
                setOpsData(finalPayload);
                setactiveAction(-1);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }

            }
            else {
                emitToasterMsg("error", "Table B.4", b4OnShorePowerSupply?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table B.4", error.errorMessage);
        }
    };

    const handleUpdateOpsOnShorePowerSupply = async ({ values, table, row }, isAuto = false) => {
        const newValidationErrors = validateOps(values, "edit", {});
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        setValidationErrors({});
        const updated = opsData.map((item, index) => {
            if (index === row.index) {
                return {
                    ...item,
                    opsEquipmentReferenceNo: values?.opsEquipmentReferenceNo,
                    opsEquipmentName: values?.opsEquipmentName,
                    technicalDetails: {
                        opsEquipmentType: values.technicalDetails?.opsEquipmentType
                            || values["technicalDetails.opsEquipmentType"]
                            || '',
                        ratingPowerKW: +values["technicalDetails.ratingPowerKW"] || 0,
                        frequencyHz: +values["technicalDetails.frequencyHz"] || 0,
                        voltageV: +values["technicalDetails.voltageV"] || 0,
                        equipmentDescription: values["technicalDetails.equipmentDescription"],
                    },
                    standard: values?.standard,
                    otherStandards: values?.otherStandards,
                };
            }
            return item;
        });
        const finalPayload = (updated).map(i => ({ ...i, b4OnshorePowerSupplyEquipmentId: 0 }));

        try {
            const b4OnShorePowerSupply = await callApi({
                method: "POST",
                url: API_ROUTES.B4(vesselMstId),
                data: finalPayload,
            });
            if (b4OnShorePowerSupply?.isSuccess) {
                emitToasterMsg("success", "Table B.4", isAuto ? "On Shore Power Supply Equipment autosaved successfully" : "On Shore Power Supply Equipment updated successfully");
                table.setEditingRow(null);
                setactiveAction(-1);
                setOpsData(finalPayload);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table B.4", b4OnShorePowerSupply?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table B.4", error.errorMessage);
        }
    };

    const handleDeleteOpsOnShorePowerSupply = async (rowIndex) => {
        const updated = opsData.filter((_, i) => i !== rowIndex);
        const finalPayload = (updated).map(i => ({ ...i }));
        try {
            const b4OnShorePowerSupply = await callApi({
                method: "POST",
                url: API_ROUTES.B4(vesselMstId),
                data: finalPayload,
            });
            if (b4OnShorePowerSupply?.isSuccess) {
                emitToasterMsg("success", "Table B.4", "On Shore Power Supply Equipment deleted successfully");
                setactiveAction(-1);
                setOpsData(finalPayload);
                setConfirmDelete(false);
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table B.4", b4OnShorePowerSupply?.errorMessage);
                setConfirmDelete(false);
            }
        } catch (error) {
            emitToasterMsg("error", "Table B.4", error.errorMessage);
        }
    };

    const handleDecimal12Digits = useCallback((e) => {
        let v = e.target.value;

        // keep digits and dot
        v = v.replace(/[^0-9.]/g, "");

        // only the first dot survives
        const dot = v.indexOf(".");
        if (dot !== -1) {
            v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, "");
        }

        // max 12 digits (dot doesn’t count)
        const digits = v.replace(".", "");
        if (digits.length > 12) {
            const allowed = digits.slice(0, 12);

            if (dot !== -1) {
                const intLen = Math.min(allowed.length, dot);   // digits before dot
                v = allowed.slice(0, intLen) + "." + allowed.slice(intLen);
            } else {
                v = allowed;
            }
        }

        e.target.value = v;
    }, []);

    const columns = useMemo(
        () => [
            {
                accessorKey: "opsEquipmentReferenceNo",
                header: "OPS equipment reference no",
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
                muiEditTextFieldProps: {
                    inputProps: {
                        maxLength: 255,
                    },
                },
            },
            {
                accessorKey: "opsEquipmentName",
                header: "OPS equipment name",
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
                muiEditTextFieldProps: {
                    inputProps: {
                        maxLength: 255,
                    },
                },
            },

            {
                id: "technicalDetails",
                header: "Technical description of OPS equipment specifed voltage and frequency, including the gear specfied in IEC/IEEE 80005-1(High Voltage) and IEC/IEEE 80005-3(Low Voltage) and position onboard",
                muiTableHeadCellProps: {
                    className: "grouped-header",
                },
                columns: [
                    {
                        accessorKey: "technicalDetails.opsEquipmentType",
                        header: "OPS equipment type",
                        editVariant: 'select',
                        editSelectOptions: getOpsEquipmentType,
                        Cell: ({ cell }) => typeLabelByCode[cell.getValue()] ?? cell.getValue(),
                        size: 220,
                        muiEditTextFieldProps: ({ row }) => ({
                            required: true,
                            error: !!validationErrors?.opsEquipementType,
                            helperText: validationErrors?.opsEquipementType,
                            onFocus: () =>
                                setValidationErrors({
                                    ...validationErrors,
                                    opsEquipementType: undefined,
                                }),
                            SelectProps: {
                                MenuProps: {
                                    disableScrollLock: true,
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 250,
                                            boxShadow: 3,
                                            maxWidth: 180,
                                            overflowY: "auto",
                                            '&::-webkit-scrollbar': {
                                                width: "8px"
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: 'darkgray',
                                                borderRadius: '3px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                        },
                                    },
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                    },
                                    transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                    },
                                    getContentAnchorEl: null,
                                }
                            }
                        }),
                        muiTableHeadCellProps: {
                            sx: {
                                borderBottom: 'none',
                                borderRight: '1px solid lightgray',
                                verticalAlign: "middle"
                            },
                        },
                    },
                    {
                        accessorKey: "technicalDetails.ratingPowerKW",
                        header: "Rating power (KW)",
                        Cell: ({ cell }) => {
                            const value = cell.getValue();
                            return value === 0 || value === '' ? '' : value;
                        },
                        muiEditTextFieldProps: ({ cell }) => ({
                            inputProps: {
                                inputMode: "numeric",
                                pattern: "\\d{0,12}(\\.\\d*)?",
                                maxLength: 13,
                                onInput: handleDecimal12Digits,
                            },
                            value: cell.getValue() === 0 ? '' : cell.getValue(),
                            onChange: (e) => {
                                const val = e.target.value === '' ? 0 : Number(e.target.value);
                                cell.row._valuesCache['technicalDetails.ratingPowerKW'] = isNaN(val) ? 0 : val;
                            }
                        }),
                    },
                    {
                        accessorKey: "technicalDetails.frequencyHz",
                        header: "Frequency (HZ)",
                        Cell: ({ cell }) => {
                            const value = cell.getValue();
                            return value === 0 || value === '' ? '' : value;
                        },
                        muiEditTextFieldProps: ({ cell }) => ({
                            inputProps: {
                                inputMode: "numeric",
                                pattern: "\\d{0,12}(\\.\\d*)?",
                                maxLength: 13,
                                onInput: handleDecimal12Digits,
                            },
                            value: cell.getValue() === 0 ? '' : cell.getValue(),
                            onChange: (e) => {
                                const val = e.target.value === '' ? 0 : Number(e.target.value);
                                cell.row._valuesCache['technicalDetails.frequencyHz'] = isNaN(val) ? 0 : val;
                            }
                        }),
                    },
                    {
                        accessorKey: 'technicalDetails.voltageV',
                        header: 'voltage (V)',
                        Cell: ({ cell }) => {
                            const value = cell.getValue();
                            return value === 0 || value === '' ? '' : value;
                        },
                        muiEditTextFieldProps: ({ cell }) => ({
                            inputProps: {
                                inputMode: "numeric",
                                pattern: "\\d{0,12}(\\.\\d*)?",
                                maxLength: 13,
                                onInput: handleDecimal12Digits,
                            },
                            value: cell.getValue() === 0 ? '' : cell.getValue(),
                            onChange: (e) => {
                                const val = e.target.value === '' ? 0 : Number(e.target.value);
                                cell.row._valuesCache['technicalDetails.voltageV'] = isNaN(val) ? 0 : val;
                            }
                        }),
                    },
                    {
                        accessorKey: "technicalDetails.equipmentDescription",
                        header: "Technical description of the OPS equipment",
                        muiEditTextFieldProps: {

                            inputProps: {
                                maxLength: 1000,
                            },
                        },
                    }
                ]
            },
            {
                accessorKey: "standard",
                header: "Standards",
                editVariant: 'select',
                editSelectOptions: getStandards,
                Cell: ({ cell }) => standardLabelByCode[cell.getValue()] ?? cell.getValue(),
                size: 220,
                muiEditTextFieldProps: ({ row }) => ({

                    SelectProps: {
                        MenuProps: {
                            disableScrollLock: true,
                            PaperProps: {
                                sx: {
                                    maxHeight: 250,
                                    boxShadow: 3,
                                    maxWidth: 180,
                                    overflowY: "auto",
                                    '&::-webkit-scrollbar': {
                                        width: "8px"
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'darkgray',
                                        borderRadius: '3px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: '#f0f0f0',
                                    },
                                },
                            },
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                            getContentAnchorEl: null,
                        }
                    }
                }),
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
            },

            {
                accessorKey: "otherStandards",
                header: "Other Standards",
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                        verticalAlign: "middle"
                    },
                },
                muiEditTextFieldProps: {
                    inputProps: {
                        maxLength: 1000,
                    },
                },
            },


        ], [
        typeLabelByCode,
        standardLabelByCode,
        validationErrors,
    ]
    )

    const table = useMaterialReactTable({
        columns,
        data: opsData || [],
        enableStickyHeader: true,
        enableSorting: false,
        enableRowSelection: false,
        enableColumnPinning: false,
        enableTopToolbar: true,
        enableBottomToolbar: false,
        enableColumnActions: false,
        createDisplayMode: "row",
        editDisplayMode: "row",
        enableEditing: true,
        enablePagination: false,
        muiTableHeadCellProps: {
            sx: {
                color: "rgb(255 255 255)",
                backgroundColor: "#042a4c",
                position: "sticky",
                borderRight: "1px solid lightgray",
                "&:last-child": {
                    borderRight: "none",
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "520px",
            },
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                muiTableHeadCellProps: {
                    sx: {
                        borderBottom: 'none',
                        borderRight: '1px solid lightgray',
                    },
                },
            },
        },
        muiTableBodyCellProps: ({ row, cell }) => {
            return {
                sx: {
                    borderRight: "1px solid lightgray",
                    "&:last-child": {
                        borderRight: "none",
                    },
                    color: "",
                },
            };
        },
        muiTableBodyRowProps: ({ row }) => ({
            sx: {
                "&:not(:last-of-type)": {
                    borderBottom: "1px solid lightgray",
                },
                verticalAlign: "top",
            },
        }),
        muiTableHeadRowProps: {
            sx: {
                borderBottom: "2px solid lightgray",
            },
        },
        columnOrder: [
            "opsEquipment",
            "opsEquipmentName",
            "technicalDetails",
            "standard",
            "otherStadards",

        ],
        onCreatingRowSave: handleCreateOpsOnShorePowerSupply,
        onEditingRowCancel: resetActiveRow,
        onCreatingRowCancel: resetActiveRow,
        onEditingRowSave: handleUpdateOpsOnShorePowerSupply,
        renderToolbarInternalActions: ({ table }) => <></>,
        renderRowActions: ({ row, table }) => {
            return (
                <Box sx={{ display: "flex", gap: "1rem" }}>
                    <Tooltip title="Edit">
                        <IconButton
                            style={{ padding: "0px" }}
                            onClick={() =>
                                handleOnShoreActions(
                                    table,
                                    row,
                                    RowActions.Edit
                                )
                            }
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            style={{ padding: "0px" }}
                            color="error"
                            onClick={() => handleDeleteBtnClick(row?.index, true)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <div className="mt-1">
                <Button
                    startIcon={<Icon icon={ICONS.ADD} />}
                    onClick={() => {
                        handleOnShoreActions(table, null, RowActions.Add);
                    }}
                    disabled={!!table.getState().creatingRow}
                >
                    Add OPS Equipment
                </Button>
            </div>
        ),
    });

    return (
        <>
            <div className="fuel-registry-b3 mt-1">
                <MaterialReactTable table={table} />
            </div>
            <div className="confirmation-modal-container">
                <Modal
                    isOpen={confirmDelete}
                    modalHeader
                    padding={"70px"}
                    headerHeight={"50px"}
                    showFooter
                    onCloseClickListener={() => {
                        setConfirmDelete(false);
                    }}
                    footer={
                        <div className="d-flex justify-content-end fixed-action-btns p-4">
                            <Button
                                className="modal-delete-btn"
                                onClick={() => { handleDeleteOpsOnShorePowerSupply(deletingRowIndex) }}
                                primaryColor={COLORS.RED.DARK}
                                primaryBackgroundColor={COLORS.RED.LIGHT}
                            >
                                Delete
                            </Button>
                            <Button
                                className="modal-cancel-btn"
                                onClick={() => {
                                    setConfirmDelete(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    }
                >
                    <div>Are you sure you want to delete this OPS equipment?</div>
                </Modal>
            </div>
        </>
    );
});

export default FeuB4OnShorePowerSupply;